/**
 * `AW17` · Borde HTTP de la carga en lote de participantes, contra las rutas de
 * SESIÓN de `AP29`.
 *
 * Tres operaciones y nada más: mandar UN archivo con su `lote` y su `brazo`,
 * listar las cargas del brazo (`CA-91`), y volver a preguntar por el reporte de
 * validación de una carga.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ① ⛔ LO QUE ENTRA DEL REPO SON SÓLO TIPOS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Misma regla que `borde-participantes.ts`, `borde-padron.ts` y
 * `proveedor-datos.ts`: **no se importa `modules/**`**, y los imports de
 * `contracts/**` son `import type`. El alias `@contracts/*` vive en
 * `tsconfig.json` y NO en `vite.config.ts`, así que importar un VALOR de un
 * contrato hace fallar el build.
 *
 * `management-ui` es un artefacto **ESTÁTICO** en S3 + CloudFront, no un
 * proceso: un import de `modules/participants` anda en el dev server de quien lo
 * escribe —hay bundler— y **no existe en producción**, donde no hay Node que lo
 * ejecute ni credencial de base que usar.
 *
 * ⚠️ Los nombres de las partes del `multipart` y el código del `404` **sí** son
 * valores del contrato, y acá se anclan con `typeof` sobre un `import type`: el
 * símbolo entra en espacio de TIPOS —no se emite ni un import en el bundle— y el
 * literal local queda obligado a coincidir. Si el contrato renombra la parte
 * `lote`, este archivo **se cae al compilar** en vez de mandar un `multipart` al
 * que le falta una parte obligatoria y leer el `400` como «algo salió mal».
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ② ⛔⭐ NINGUNA CREDENCIAL DE MÁQUINA. LAS RUTAS SON LAS DE SESIÓN.
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `borde-participantes.ts` ya dejó escrito el argumento: «un token que el
 * JavaScript tuviera que leer para ponerlo en un header es un token que la
 * pantalla **tuvo que GUARDAR**, y esta unidad no guarda nada».
 *
 * El atajo que hay que no tomar es el más corto de todo este frente y por eso se
 * nombra: **el endpoint de ingesta YA EXISTE** —`POST participantes`,
 * `contracts/participants/http.ts` ⟦http-ruta-ingesta⟧, implementado y andando—,
 * así que esta pantalla podría pegarle derecho poniendo el header de la
 * credencial de máquina, y `AP29` entera sobraría. Lo que produce es el secreto
 * de ingesta de **Elige** adentro de un bundle servido por un bucket público:
 * legible para cualquiera que abra las herramientas de desarrollo.
 *
 * ⛔ **Y las dos variantes que suenan prudentes y no lo son:**
 *
 *   · que la credencial venga en `configuracion.json` «así no está en el
 *     bundle» — es el MISMO archivo servido por el MISMO bucket público: no
 *     cambia quién lo puede leer, sólo dónde hay que mirar;
 *   · que la pantalla le pegue a la ruta de máquina **sin** credencial y espere
 *     que `admin-web` proxee — la fila de auditoría de D5 quedaría con el actor
 *     de la MÁQUINA para un acto HUMANO, que es el `guard` ① de `AP29`.
 *
 * ⇒ acá hay `credentials: "include"` y **ningún header de autorización armado a
 * mano**, en las tres operaciones. El `actor` y el `brazo de sesión` los resuelve
 * `admin-web`; esta unidad no los puede escribir ni tiene dónde guardarlos.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * ③ ⛔⚠️ NI UN TELÉFONO NI UN NOMBRE CRUZAN ESTE BORDE HACIA LA PANTALLA
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * `IngestRowOutcome` lleva **el teléfono** en `duplicada` y `cruzada`, y **el
 * nombre crudo y el saneado** en `saneada`, de estudiantes chilenos de los que
 * muchos son menores. `contracts/participants/index.ts` deja las dos lecturas
 * abiertas **con dueño el TL, con legal si aplica**: ① el reporte entero con el
 * dato personal, ② el **desenlace y el número de fila**, sin el dato personal —
 * «la persona que subió el archivo **lo tiene**: puede cruzar por número de fila
 * contra su propio CSV».
 *
 * ⛔ **`ADR-0191` decidió la proyección del PADRÓN, no la del REPORTE, y no se
 * estira.** Esta unidad implementa la lectura **②**, que es la mitad que **no
 * depende de la respuesta del TL** y que **alcanza para que `F8.md` `P0-2` se
 * cumpla**. Si el TL contesta ①, la pantalla **gana** dos columnas —se agregan
 * los campos al `Pick` de `FilaDelReporte` y `tsc` señala dónde llenarlas—; si
 * contesta ②, **no pierde ninguna**.
 *
 * ⭐ **Y el recorte se hace ACÁ, en el borde, no en el componente**, por el mismo
 * motivo por el que `borde-padron.ts` arma su fila campo por campo: lo que no
 * entra a la memoria de la pantalla no lo puede dibujar un `Object.entries` de
 * mañana, ni aparecer en un volcado de estado, ni colarse en una prueba. La
 * pantalla **no tiene de dónde** sacar un teléfono. Acá el teléfono y los
 * nombres no se leen **ni para validarlos**.
 */

import type {
  Carga,
  EstadoDeCarga,
  FilaRechazada,
  IngestReport,
  IngestRowOutcome,
} from "@contracts/participants/index.ts"
import type {
  IngestAcceptedResponseBody,
  IngestUploadId,
  PARTICIPANTS_INGEST_FILE_FIELD,
  PARTICIPANTS_INGEST_LOTE_FIELD,
  PARTICIPANTS_INGEST_UPLOAD_ID_FIELD,
} from "@contracts/participants/http.ts"
/*
  ⛔ `R3-UI5` · `B23` · **`PARTICIPANTS_SESSION_STATUS` entra como VALOR, no como
  tipo, y `tsc` lo hizo evidente** (`TS1361`): `B23` exige que el `202` aceptado se
  compare contra `…ingestAccepted`, y un `import type` deja el símbolo en espacio
  de tipos —la comparación no compila, y la salida cómoda es volver al literal
  `202`, que es exactamente el defecto que el caso persigue—. Los cinco de arriba
  siguen en `import type` porque sólo se usan en `typeof`.
*/
/*
  ⟦rastro · ADR-0082 · 29/9/2026 · humo de B⟧ Lo de arriba dejó de ser cierto: «entra
  como VALOR, no como tipo». Un import de VALOR desde `contracts/` no lo resuelve
  Vite (`vite.config.ts:10-15`: no hay alias a `contracts/` a propósito) y dejó la
  SPA ENTERA en blanco — `tsc` y `bun test` resuelven el alias y no lo vieron; lo
  midió el humo (`qa/_ejecucion-R3/humo-B/corrida/20260929T171101Z`). Ahora entra
  como TIPO y el `202` es una constante local TIPADA por el contrato
  (`ACEPTADA`), la misma forma que `REPORTE_TODAVIA_NO`: si el contrato cambia el
  código, `tsc` se cae acá, y el literal no queda suelto.
*/
import type { PARTICIPANTS_SESSION_STATUS } from "@contracts/participants/http.ts"
import type { ConfiguracionInstancia } from "../configuracion.ts"
import { mensajeDeError } from "../mensaje-de-error.ts"
import { raizDelBrazo } from "../brazo-del-cable.ts"

// ── El acuerdo de cable ──────────────────────────────────────────────────────

/**
 * Las dos rutas de SESIÓN, **sin ancla de tipo y a sabiendas**.
 *
 * `contracts/participants/http.ts` declara la superficie de **MÁQUINA**
 * —`POST participantes` y `GET participantes/reporte/{uploadId}`, las dos con
 * `PARTICIPANTS_MACHINE_CREDENTIAL_HEADER`— y **no declara ninguna ruta de
 * sesión para la ingesta**. Las dos de acá son, igual que `padron`, `elegibles`
 * y `documentos`, **una invención de `apps/admin-web`** (`AP29`), y el
 * precedente de esta app para exactamente este caso es `borde-padron.ts`
 * (`RUTA_PADRON`): el segmento va como literal sin tipo, y se dice acá que **no
 * lo protege nada**. Si `admin-web` las renombra, esto compila igual y falla con
 * un `404`.
 *
 * ⚠️ Inventarles un alias local (`type CargasPath = "cargas"`) sería un ancla de
 * utilería: se verifica contra sí misma y no puede ponerse roja nunca.
 *
 * ⛔ **NO son `participantes` ni `participantes/reporte/{uploadId}`**, y el
 * motivo no es de gusto: esas dos las atrapa el plano de MÁQUINA en
 * `apps/admin-web/src/rutas.ts`, ARRIBA de `resolverActor` y exigiendo la
 * credencial de máquina. Pegarles desde acá es el `guard` ② de este archivo.
 *
 * ⚠️ **El sub-recurso va UN NIVEL MÁS ABAJO que `{uploadId}`** —`cargas/{id}/reporte`
 * y no `cargas/reporte/{id}`— porque así no hay un segmento literal hermano de
 * `{uploadId}` que un `uploadId` llamado `reporte` pueda secuestrar. Es la forma
 * contraria a la del plano de máquina, y es deliberada (`AP29`).
 */
const RUTA_CARGAS = "cargas"
const SEGMENTO_REPORTE = "reporte"

/**
 * Los nombres de las TRES partes del `multipart`, anclados contra el contrato
 * **menos el `brazo`, que desde `R3-UI5` va sin ancla** (ver `CAMPO_BRAZO`).
 *
 * `typeof` sobre un `import type`: el símbolo entra sólo en espacio de tipos —no
 * se emite ningún import en el bundle, que es lo que el `guard` ① exige— y el
 * literal local queda obligado a coincidir con el del contrato. Es la misma
 * disciplina que `RUTA_ALTA: ParticipantsEnrollPath` en `borde-participantes.ts`
 * y que los cinco `keyof MasterQuery` de `borde-padron.ts`.
 *
 * Defiende justo el caso silencioso: una parte renombrada **no da `404`**, da un
 * `400` que en pantalla se lee como «el archivo no se pudo mandar», sin decir
 * nunca que el que está mal es el cliente.
 */
const CAMPO_ARCHIVO: typeof PARTICIPANTS_INGEST_FILE_FIELD = "archivo"
const CAMPO_LOTE: typeof PARTICIPANTS_INGEST_LOTE_FIELD = "lote"

/**
 * ⛔ `R3-UI5` · `B23` — **el `brazo` es el ÚNICO de los tres nombres de parte que
 * queda SIN ancla de tipo, y es a sabiendas.**
 *
 * Hasta `R3-CPA2` esta línea decía
 * `typeof PARTICIPANTS_INGEST_BRAZO_FIELD = "brazo"`: se anclaba al plano de
 * MÁQUINA. `PARTICIPANTS_SESSION_STATUS` (`contracts/participants/http.ts`) es el
 * símbolo de SESIÓN que reemplaza a `PARTICIPANTS_API_STATUS` **para los códigos
 * de estado**, y el lector confirmó que **no existe ningún `*_FIELD` del lado de
 * sesión**: los siete `*_FIELD`/`*_PARAM` que nombran `archivo`/`lote`/`brazo`
 * siguen colgando del bloque `multipart` del plano de máquina
 * (`PARTICIPANTS_INGEST_FILE_FIELD`, `PARTICIPANTS_INGEST_LOTE_FIELD`,
 * `PARTICIPANTS_INGEST_BRAZO_FIELD`, `PARTICIPANTS_ENROLL_*_FIELD`,
 * `PARTICIPANTS_FLAG_BRAZO_FIELD`, `PARTICIPANTS_REPORT_BRAZO_PARAM`), y
 * `PARTICIPANTS_REPORT_BRAZO_PARAM` está `@deprecated (R3-CPA2 B23)`.
 *
 * ⛔ **Anclar el `brazo` al plano de máquina es exactamente el defecto que `B23`
 * nombra**, así que el ancla se cae con el símbolo baneado y **no se reemplaza
 * por otra del mismo plano** (`PARTICIPANTS_ENROLL_BRAZO_FIELD` sería peor:
 * nombraría el cuerpo del ALTA desde el borde de la CARGA).
 *
 * ⚠️ El literal sigue siendo el correcto —`brazo`, igual que los otros dos— pero
 * **no lo protege nada**: si `admin-web` renombra la parte, esto compila igual y
 * el `400` se lee como «el archivo está mal». Es el mismo trato que ya tienen
 * `RUTA_CARGAS` y `SEGMENTO_REPORTE` acá arriba, y **el día que `R3-CPA2` publique
 * un `PARTICIPANTS_SESSION_INGEST_BRAZO_FIELD`, esta línea vuelve a llevar ancla**.
 */
const CAMPO_BRAZO = "brazo"

/**
 * El campo del cuerpo del `202`, anclado por partida DOBLE: contra la constante
 * del contrato y contra la clave real de `IngestAcceptedResponseBody`. Si el
 * contrato renombrara una sola de las dos, este archivo se cae.
 */
const CAMPO_UPLOAD_ID: typeof PARTICIPANTS_INGEST_UPLOAD_ID_FIELD &
  keyof IngestAcceptedResponseBody = "uploadId"

/**
 * ⭐ El `404` del reporte, anclado contra
 * `PARTICIPANTS_SESSION_STATUS.reportOrParticipantNotFound`.
 *
 * ⛔ **Y significa «TODAVÍA NO», no «no hay».** El contrato lo declara textual:
 * «Esta proyección **no distingue “todavía no” de “nunca va a existir”**: el
 * puerto tampoco lo distingue […] y ⟦http-principio⟧ prohíbe que la proyección
 * invente una distinción que el puerto no produjo».
 *
 * Por eso `leerReporte` devuelve **`null`** y no lanza: un `throw` acá haría que
 * la pantalla dibujara el camino de ERROR —el mismo que el `503` y el `401`— y
 * el operador leería «falló» sobre una carga que fue aceptada y se está
 * procesando. `null` es «el borde contestó bien, y lo que contestó es que
 * todavía no está».
 */
const REPORTE_TODAVIA_NO: typeof PARTICIPANTS_SESSION_STATUS.reportOrParticipantNotFound = 404
/** El `202` de «archivo recibido, cifrado y encolado» del plano de SESIÓN (B23), tipado por el contrato. */
const ACEPTADA: typeof PARTICIPANTS_SESSION_STATUS.ingestAccepted = 202

// ── Lo que la pantalla conoce del reporte ────────────────────────────────────

/**
 * ⭐⛔ Una fila del reporte **SIN el dato personal** — la lectura ② del bloque ③.
 *
 * Cada variante se declara con un `Pick` sobre la variante del contrato, y **no
 * con campos escritos a mano**, por dos motivos que se pagan solos:
 *
 *   · **la omisión queda NOMBRADA**: `duplicada` lista `"outcome" | "row"` y no
 *     `"telefono"`, así que quien lea este tipo ve *qué* se está dejando afuera,
 *     en vez de tener que comparar contra el contrato para descubrirlo;
 *   · **los tipos siguen atados**: el día que `reason`, `otherArm` o
 *     `columnCount` cambien de tipo o de nombre en `contracts/participants`,
 *     este archivo **se cae al compilar**. Con campos a mano, seguiría
 *     compilando en exit 0 y dibujando `undefined`.
 *
 * ⇒ **el día que el TL conteste la lectura ①**, esta pantalla gana sus columnas
 * agregando `"telefono"` acá y `"rawName" | "sanitizedName"` en `saneada`: `tsc`
 * marca entonces cada sitio que hay que completar. **No pierde nada por haber
 * empezado por ②.**
 */
export type FilaDelReporte =
  | Pick<Extract<IngestRowOutcome, { outcome: "rechazada" }>, "outcome" | "row" | "reason">
  /** ⛔ Sin `rawName` ni `sanitizedName`: son el nombre de una persona. */
  | Pick<Extract<IngestRowOutcome, { outcome: "saneada" }>, "outcome" | "row">
  /** ⛔ Sin `telefono`. */
  | Pick<Extract<IngestRowOutcome, { outcome: "duplicada" }>, "outcome" | "row">
  /** ⛔ Sin `telefono`. `otherArm` es un nombre de BRAZO y no un dato de nadie. */
  | Pick<Extract<IngestRowOutcome, { outcome: "cruzada" }>, "outcome" | "row" | "otherArm">
  /**
   * ⛔ Sin los valores de las columnas de más: `ADR-0106` deja escrito que una
   * columna de más puede ser señal de una fila CORRIDA, y ese dato —si la fila
   * está corrida— **pertenece a la persona equivocada**.
   */
  | Pick<Extract<IngestRowOutcome, { outcome: "excedida" }>, "outcome" | "row" | "columnCount">

/** Los cinco desenlaces, como valores, para recorrerlos sin repetir la lista. */
export const DESENLACES_DE_FILA = [
  "rechazada",
  "saneada",
  "duplicada",
  "cruzada",
  "excedida",
] as const

/**
 * ⭐ Testigo de compilación: **los cinco desenlaces del contrato están cubiertos,
 * y no hay ninguno de más**.
 *
 * Sin esto, el día que el contrato agregue un sexto desenlace por fila, el
 * lector de runtime lo descartaría, la fila **desaparecería del reporte** y
 * `tsc` seguiría en cero: exactamente el modo de falla que el propio contrato
 * midió para `crossArm` —«sigue compilando en exit 0 […] sin que nada se ponga
 * rojo»—, del lado de la pantalla.
 *
 * Las dos direcciones, y en una tupla para que `never` no se distribuya.
 */
type DesenlacesQueFaltan = Exclude<IngestRowOutcome["outcome"], FilaDelReporte["outcome"]>
type DesenlacesQueSobran = Exclude<FilaDelReporte["outcome"], IngestRowOutcome["outcome"]>
type DesenlacesSinLista = Exclude<IngestRowOutcome["outcome"], (typeof DESENLACES_DE_FILA)[number]>
const DESENLACES_COMPLETOS: [DesenlacesQueFaltan, DesenlacesQueSobran, DesenlacesSinLista] extends
  [never, never, never]
  ? true
  : false = true
void DESENLACES_COMPLETOS

/**
 * El reporte tal como la pantalla lo conoce: **el `IngestReport` entero salvo
 * `rows`, que va proyectado sin el dato personal**.
 *
 * ⭐ **`Omit` y no `Pick`, y la diferencia es la que importa.** Con un `Pick` de
 * los ocho campos de hoy, un campo NUEVO del reporte —otro hallazgo, otro
 * contador— entraría por el cable y esta pantalla **lo escondería en silencio**,
 * con `tsc` en cero. Con `Omit`, el campo nuevo aparece en el tipo, el objeto
 * literal de `leerReporte` deja de satisfacerlo y **este archivo se cae al
 * compilar**, que es cuando alguien decide qué hacer con él. Una pantalla cuyo
 * trabajo es no esconder la mitad del reporte no puede tener, en su propio tipo,
 * la puerta por la que se esconde la mitad siguiente.
 */
export type ReporteDeCarga = Omit<IngestReport, "rows"> & {
  readonly rows: readonly FilaDelReporte[]
}

/** Lo que el operador declara para UNA carga. */
export type PedidoDeCarga = {
  /** UN archivo. `contracts/participants/http.ts`: «la ingesta es de UN archivo por pedido». */
  readonly archivo: File
  /** ⭐ Del REQUEST, nunca del nombre del archivo (`F8.md` `P0-1`). */
  readonly lote: string
  /** ⭐ Del REQUEST, nunca del nombre del archivo (`F8.md` `P0-1`). */
  readonly brazo: string
}

/**
 * El borde de la carga en lote visto desde el navegador.
 *
 * ⭐ **`leerReporte` devuelve `ReporteDeCarga | null` y `null` es «TODAVÍA NO»**, no «no
 * hay». Ver `REPORTE_TODAVIA_NO`. Quien consuma este borde tiene que decir eso y
 * no otra cosa; el tipo no puede impedirlo, así que está escrito en los dos
 * lugares.
 *
 * ⭐ `R3-UI5`: **el reporte de una carga es la `Carga` misma.** `RT 7`/`RT 9` la
 * declaran con sus contadores (`cargadas`, `rechazadas`, `yaEnElOtroBrazo`), su
 * `estado` y sus `filasRechazadas`, y la ruta de reporte devuelve esa misma
 * fila. No hay una proyección aparte que mantener en sincronía.
 */
export type BordeCarga = {
  /** Manda UN archivo y devuelve el `uploadId` del `202`. */
  readonly cargar: (pedido: PedidoDeCarga) => Promise<IngestUploadId>
  /**
   * ⭐ `R3-UI5` · `CA-91` — **las cargas del brazo, del SERVIDOR.**
   *
   * ⛔ La lista NO sale de la memoria de esta pestaña: una lista local olvida lo
   * que cargó otra persona y lo que había antes de recargar, y la tabla de
   * `RT 7`/`RT 9` es la evidencia de las cargas REALES del brazo. El servidor es
   * el único que las tiene todas.
   */
  readonly listarCargas: () => Promise<readonly Carga[]>
  /**
   * Pide el reporte de UNA carga. `null` = todavía no está.
   *
   * ⚠️ **Deuda declarada (`R3-UI5`):** el panel de `R3` NO se alimenta de acá
   * —sale de la fila de `listarCargas()`, que es una `Carga` con los tres
   * contadores y `filasRechazadas`—. Este método y el tipo `ReporteDeCarga`
   * quedaron del plano de máquina de `R1.1` y **se retiran cuando
   * `pruebas/aw17-carga-en-lote.test.tsx` se termine de reescribir**: sus casos
   * de reporte todavía programan las cinco variantes de fila
   * (`rechazada`/`saneada`/`duplicada`/`cruzada`/`excedida`) y el `crossArmScan`,
   * que `Carga` no tiene. Retirarlos antes deja ese examen sin compilar.
   *
   * ⛔ Acá había un conteo de líneas de `aw17` («de 1.038 líneas», después
   * «de 822»). Se sacó: un número que se copia de una corrida queda viejo en
   * cuanto alguien toca el archivo, y una nota no es un invariante. La razón
   * —«retirarlos antes rompe la compilación de ese examen»— no necesita el
   * tamaño para sostenerse.
   */
  readonly leerReporte: (uploadId: IngestUploadId) => Promise<ReporteDeCarga | null>
}

// ── Lectura en RUNTIME de lo que llega por el cable ──────────────────────────

/** Error del borde con la forma que el resto de la app ya usa (`HttpError`). */
function errorDelBorde(mensaje: string, statusCode: number): Error & { statusCode: number } {
  return Object.assign(new Error(mensaje), { statusCode })
}

function esObjeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === "object" && valor !== null
}

function numeroDe(valor: unknown): number | null {
  return typeof valor === "number" && Number.isFinite(valor) ? valor : null
}

function cadenaDe(valor: unknown): string | null {
  return typeof valor === "string" ? valor : null
}

/**
 * ⭐ `R3-UI5` · `CA-97` — **una fila rechazada, CON SU NOMBRE Y SU TELÉFONO.**
 *
 * ⛔ **Acá se da vuelta lo que el bloque ③ del encabezado declaraba** («ni un
 * teléfono ni un nombre cruzan este borde»), y se da vuelta porque **quien lo
 * pidió cambió**: `contracts/participants/index.ts` declara en `FilaRechazada`,
 * textual, *«Lleva el nombre y el teléfono TAL COMO VENÍAN en el archivo, vacíos
 * si la fila no los trae. El reporte va sin cifrar con nombre y teléfono
 * (decisión de la PO), y `ADR-0201` no lo alcanza porque el reporte es un `200`,
 * no un 4xx»*.
 *
 * ⭐ Y el motivo es de operación, no de comodidad: **la fila rechazada se rechazó
 * por su teléfono o por su nombre**, y `CA-97` pide que el operador pueda cruzar
 * la fila contra SU archivo y corregirla. Proyectada sin el dato, la fila
 * «rechazada 4.312» no le dice qué arreglar. **El operador ya tiene el archivo:
 * el dato no se lo estamos revelando, se lo estamos devolviendo.**
 *
 * ⚠️ Se lee campo por campo igual que las otras filas: `""` es un valor válido
 * —la fila puede no traer el nombre— y **no se confunde con «falta el campo»**,
 * que es `null` y descarta la carga (ver `leerCarga`). ⛔ Acá NO se convierte a
 * «—»: el «—» es dibujo del panel (`reporte.tsx`, `PRD_R3.md:2138`), y el borde
 * devuelve el dato crudo o `null`, nunca un texto de pantalla.
 */
function leerFilaRechazada(valor: unknown): FilaRechazada | null {
  if (!esObjeto(valor)) return null
  const numero = numeroDe(valor["numero"])
  const nombre = cadenaDe(valor["nombre"])
  const telefono = cadenaDe(valor["telefono"])
  const motivo = cadenaDe(valor["motivo"])
  if (numero === null || nombre === null || telefono === null || motivo === null) return null
  return { numero, nombre, telefono, motivo }
}

/**
 * ⭐ `R3-UI5` · **una `Carga` del contrato de R3, comprobada en RUNTIME.**
 *
 * Es el sujeto nuevo de esta etapa: la lista (`CA-91`) y el reporte del panel
 * (`CA-94`…`CA-98`) son **la misma cosa** —una fila de `cargas`— leída en dos
 * lugares. Se comprueba campo por campo por el mismo motivo que el resto de los
 * lectores de este archivo: `await respuesta.json()` devuelve `unknown`, y un
 * cast borraría la única garantía que el contrato se tomó el trabajo de
 * declarar.
 *
 * ⛔ **Una carga que no se entiende invalida la lista entera y no se descarta en
 * silencio.** Es la misma decisión que `leerReporteDeCarga` tomó para las filas:
 * una lista a la que le falta la carga que el operador acaba de mandar se ve
 * idéntica a una donde esa carga todavía no llegó, y es justo la que él está
 * esperando. `null` no es «no hay»: es «esto no se entiende».
 *
 * ⭐ **`aceptadaEn` viaja como ISO y se convierte acá.** El contrato lo declara
 * `Date`; por el cable llega una cadena. La conversión vive en el borde y no en
 * la pantalla, para que la pantalla reciba `Date` y no tenga que adivinar el
 * formato — que es exactamente el defecto que `CA-90` («fecha y hora de Chile»)
 * mide del otro lado.
 */
function leerCarga(valor: unknown): Carga | null {
  if (!esObjeto(valor)) return null

  const id = cadenaDe(valor["id"])
  const arm = cadenaDe(valor["arm"])
  const lote = cadenaDe(valor["lote"])
  const archivo = cadenaDe(valor["archivo"])
  const actor = cadenaDe(valor["actor"])
  const cargadas = numeroDe(valor["cargadas"])
  const rechazadas = numeroDe(valor["rechazadas"])
  const yaEnElOtroBrazo = numeroDe(valor["yaEnElOtroBrazo"])
  if (
    id === null ||
    arm === null ||
    lote === null ||
    archivo === null ||
    actor === null ||
    cargadas === null ||
    rechazadas === null ||
    yaEnElOtroBrazo === null
  ) {
    return null
  }

  /**
   * ⛔ `"procesando" | "lista"` y **nada más**: un tercer estado que viniera del
   * cable no puede dibujarse como uno de los dos. `EstadoDeCarga` lo declara
   * —«no hay un tercero derivado de la cola»— y el `switch` de la tabla tiene que
   * poder ser exhaustivo sobre estos dos.
   */
  const crudo = valor["estado"]
  const estado: EstadoDeCarga | null =
    crudo === "procesando" ? "procesando" : crudo === "lista" ? "lista" : null
  if (estado === null) return null

  const aceptadaEnCruda = cadenaDe(valor["aceptadaEn"])
  if (aceptadaEnCruda === null) return null
  const aceptadaEn = new Date(aceptadaEnCruda)
  if (Number.isNaN(aceptadaEn.getTime())) return null

  /**
   * `motivo` es `string | null` y **`undefined` no es `null`**: a `null` —«está
   * bien»— se llega sólo si el campo vino y vino `null`.
   */
  const motivoCrudo = valor["motivo"]
  const motivo = motivoCrudo === null ? null : cadenaDe(motivoCrudo)
  if (motivoCrudo !== null && motivo === null) return null

  const filasCrudas = valor["filasRechazadas"]
  if (!Array.isArray(filasCrudas)) return null
  const filasRechazadas: FilaRechazada[] = []
  for (const filaCruda of filasCrudas) {
    const fila = leerFilaRechazada(filaCruda)
    if (fila === null) return null
    filasRechazadas.push(fila)
  }

  return {
    id,
    arm,
    lote,
    archivo,
    actor,
    aceptadaEn,
    estado,
    cargadas,
    rechazadas,
    yaEnElOtroBrazo,
    filasRechazadas,
    motivo,
  }
}

/**
 * ⭐ `R3-UI5` · `CA-91` — la lista del brazo, comprobada en RUNTIME.
 *
 * ⛔ Devolver `null` en vez de `[]` cuando algo no se entiende: **una lista vacía
 * es una afirmación** —«este brazo no tiene cargas»— y `CA-109` pide que la
 * pantalla la diga con esas palabras. Si el cuerpo viniera roto, `[]` haría que
 * la pantalla dijera «todavía no hay cargas» sobre una respuesta que no se
 * entendió. Son dos cosas distintas y el tipo no las puede distinguir.
 */
export function leerCargas(valor: unknown): readonly Carga[] | null {
  if (!Array.isArray(valor)) return null
  const cargas: Carga[] = []
  for (const cruda of valor) {
    const carga = leerCarga(cruda)
    if (carga === null) return null
    cargas.push(carga)
  }
  return cargas
}

/**
 * Una fila del reporte, comprobada en RUNTIME y **recortada**.
 *
 * `await respuesta.json()` devuelve `unknown`, así que un cast borraría la única
 * garantía que el contrato se tomó el trabajo de declarar: que la pantalla puede
 * distinguir los cinco desenlaces **sin parsear texto**. Es el mismo criterio
 * que `leerEnrollOutcome` de `borde-participantes.ts` ya aplica a `EnrollOutcome`.
 *
 * ⛔ **El teléfono y los nombres no se leen ni una vez** — ni para validarlos.
 * Cada rama arma un objeto literal campo por campo con lo que la lectura ② deja
 * cruzar, y nada más: sin `...fila`, sin `omit()`, sin helper por reflexión. Las
 * tres devuelven un valor NO literal, y el chequeo de propiedades excedentes de
 * TypeScript **no mira un valor que no es un literal** — que es el defecto que
 * `borde-padron.ts` §② midió para la fila del padrón.
 */
export function leerFilaDelReporte(valor: unknown): FilaDelReporte | null {
  if (!esObjeto(valor)) return null
  const outcome = valor["outcome"]
  const row = numeroDe(valor["row"])
  if (row === null) return null

  if (outcome === "rechazada") {
    const reason = valor["reason"]
    return typeof reason === "string" ? { outcome, row, reason } : null
  }
  if (outcome === "saneada") return { outcome, row }
  if (outcome === "duplicada") return { outcome, row }
  if (outcome === "cruzada") {
    const otherArm = valor["otherArm"]
    return typeof otherArm === "string" ? { outcome, row, otherArm } : null
  }
  if (outcome === "excedida") {
    const columnCount = numeroDe(valor["columnCount"])
    return columnCount === null ? null : { outcome, row, columnCount }
  }
  return null
}

/**
 * El `CrossArmScan`, comprobado en RUNTIME **con sus dos mitades**.
 *
 * ⭐ `otherArms` es obligatorio y **no cae a `[]` cuando falta**: un default
 * vacío convertiría «el servidor no mandó el alcance» en «no se miró ningún otro
 * brazo», que es una afirmación sobre el RCT que nadie hizo. Si el alcance no
 * viene, el reporte entero no se entiende y se dice eso.
 */
function leerCruce(valor: unknown): ReporteDeCarga["crossArmScan"] | null {
  if (!esObjeto(valor)) return null
  const otherArms = valor["otherArms"]
  const matches = numeroDe(valor["matches"])
  if (!Array.isArray(otherArms) || matches === null) return null
  if (!otherArms.every((brazo): brazo is string => typeof brazo === "string")) return null
  return { otherArms: [...otherArms], matches }
}

/**
 * El reporte entero, comprobado en RUNTIME.
 *
 * Una fila que no se entiende **invalida el reporte** en vez de descartarse en
 * silencio: un reporte al que le falta la fila 4.312 se ve idéntico a uno donde
 * la 4.312 entró sin incidentes, y es justo la fila que el operador tiene que
 * ir a mirar. `contracts/participants/index.ts` lo dice de los contadores —«un
 * contador al lado de la lista es exactamente cómo se llega a un reporte que
 * dice una cosa y enumera otra»— y acá vale igual.
 */
export function leerReporteDeCarga(valor: unknown): ReporteDeCarga | null {
  if (!esObjeto(valor)) return null

  const reportId = valor["reportId"]
  if (typeof reportId !== "string") return null

  const crudas = valor["rows"]
  if (!Array.isArray(crudas)) return null
  const rows: FilaDelReporte[] = []
  for (const cruda of crudas) {
    const fila = leerFilaDelReporte(cruda)
    if (fila === null) return null
    rows.push(fila)
  }

  const total = numeroDe(valor["total"])
  const ingested = numeroDe(valor["ingested"])
  const rejected = numeroDe(valor["rejected"])
  const sanitized = numeroDe(valor["sanitized"])
  const duplicates = numeroDe(valor["duplicates"])
  if (
    total === null ||
    ingested === null ||
    rejected === null ||
    sanitized === null ||
    duplicates === null
  ) {
    return null
  }

  const crossArmScan = leerCruce(valor["crossArmScan"])
  if (crossArmScan === null) return null

  /**
   * `headerMismatch` es `string | null` y **`undefined` no es `null`**: un
   * reporte al que le falta el campo se leería como «el encabezado matcheó», que
   * es justo la afirmación que `ADR-0108` existe para no dejar inventar.
   */
  const headerMismatch = valor["headerMismatch"]
  if (headerMismatch !== null && typeof headerMismatch !== "string") return null

  return {
    reportId,
    rows,
    total,
    ingested,
    rejected,
    sanitized,
    duplicates,
    crossArmScan,
    headerMismatch,
  }
}

/** El `uploadId` del cuerpo del `202`, comprobado en RUNTIME. */
export function leerUploadId(valor: unknown): IngestUploadId | null {
  if (!esObjeto(valor)) return null
  const uploadId = valor[CAMPO_UPLOAD_ID]
  /**
   * La cadena vacía se rechaza: un `uploadId` vacío arma la ruta
   * `cargas//reporte`, que no es la de nadie, y el operador vería un `404`
   * —o sea «todavía no»— para siempre, sobre una carga que sí entró.
   */
  return typeof uploadId === "string" && uploadId !== "" ? uploadId : null
}

// ── El borde ────────────────────────────────────────────────────────────────

/**
 * Arma el borde de carga en lote contra la `admin-web` configurada.
 *
 * Función y no módulo con estado, por lo mismo que los otros cinco bordes: la
 * dirección llega en tiempo de ejecución (`configuracion.json`) y no hay ningún
 * momento de import en el que conocerla.
 */
export function crearBordeCargaHttp(config: ConfiguracionInstancia): BordeCarga {
  /**
   * ⭐ `AR5-m` · `R3-UI5` · ⟦el-brazo-entra-por-ruta⟧ · **el brazo va DENTRO de la
   * plantilla de la URL, junto al segmento de las cargas.**
   *
   * Es una FUNCIÓN y no una constante porque el brazo sale de la DIRECCIÓN
   * (`raizDelBrazo(config.adminWebUrl)`; `brazo-del-cable.ts` tiene la decisión
   * del dueño citada), y la dirección cambia sin que este borde se vuelva a
   * construir (`App.tsx` lo memoiza por `config`).
   *
   * ⛔ **El brazo y el segmento se escriben juntos, no en dos pasos.** Partirlo
   * —una `raiz()` que devuelve la base y un `ruta` que se le pega después— deja
   * la palabra `brazo` fuera de la plantilla del pedido: quien lea el borde para
   * ver si las cargas van al brazo de la dirección no la encuentra, y concluye
   * —con razón, sobre ese texto— que el pedido no lleva brazo.
   */
  const rutaDeLasCargas = (sufijo = ""): string =>
    `${raizDelBrazo(config.adminWebUrl)}/${RUTA_CARGAS}${sufijo}`

  const pedir = async (url: string, init: RequestInit): Promise<Response> => {
    try {
      /*
       * `credentials: "include"` y ningún `Authorization` armado acá. La sesión
       * es una cookie `HttpOnly` que el navegador manda sola (`ADR-0006`,
       * Better Auth + MFA). Ver el bloque ② del encabezado.
       */
      return await fetch(url, { ...init, credentials: "include" })
    } catch (causa) {
      throw Object.assign(errorDelBorde(`No se pudo hablar con el servidor (${config.adminWebUrl}).`, 0), {
        cause: causa,
      })
    }
  }

  return {
    /**
     * `POST cargas` — UN archivo, con su `lote` y su `brazo`.
     *
     * ⭐ **UN archivo por pedido**, y no es una simplificación: el molde de esta
     * app para subir archivos —`banco-de-contenido/modal-carga.tsx` (`AW2`)—
     * manda VARIOS en un request, y acá eso sería un pedido que el borde
     * rechaza, porque `contracts/participants/http.ts` ⟦http-ruta-ingesta⟧
     * declara «una sola parte: la ingesta es de **UN archivo por pedido**
     * (`F8-P0-1`)» y `PARTICIPANTS_API_STATUS.missingOrExtraPart` es el `400`
     * de «tiene partes de más».
     *
     * ⛔ **No se toca `archivo.name` ni una vez.** Ni para derivar el `lote`, ni
     * para sugerir el `brazo`, ni para «validar» nada: `F8.md` `P0-1` es textual
     * —«el lote y el brazo se toman del request, **no del nombre del archivo**»—
     * y `apps/admin-web/src/ingesta.ts` lo repite del otro lado: «Acá no hay
     * ninguna línea que mire `parte.name` del archivo».
     *
     * ⚠️ **El `Content-Type` NO se pone a mano.** `multipart/form-data` lleva un
     * `boundary` que sólo conoce el `FormData` que el navegador está por
     * serializar: un header escrito acá lo pisa sin boundary y el borde recibe
     * un cuerpo que no puede partir — un `400` que se lee como «el archivo está
     * mal». Es lo mismo que hace `borde-escritura.ts` para el banco.
     */
    async cargar(pedido: PedidoDeCarga): Promise<IngestUploadId> {
      const cuerpo = new FormData()
      cuerpo.append(CAMPO_ARCHIVO, pedido.archivo)
      cuerpo.append(CAMPO_LOTE, pedido.lote)
      cuerpo.append(CAMPO_BRAZO, pedido.brazo)

      const respuesta = await pedir(rutaDeLasCargas(), {
        method: "POST",
        headers: { Accept: "application/json" },
        body: cuerpo,
      })

      /*
       * ⭐ `R3-UI5` · `B23` — el ÚNICO código aceptado se nombra con el símbolo de
       * SESIÓN. `PARTICIPANTS_SESSION_STATUS.ingestAccepted` es «archivo recibido,
       * cifrado y encolado», que es exactamente lo que este pedido significa.
       *
       * ⛔ Comparar contra el literal `202` era la mitad de `B23`: el símbolo
       * quedaba de adorno y el código del plano seguía duplicado acá. Un `2xx` que
       * NO sea el aceptado —un `200` de un proxy, por ejemplo— no puede pasar por
       * «cargado»: el `uploadId` que trajera no sería el de una carga encolada.
       */
      if (respuesta.status !== ACEPTADA) {
        /**
         * ⭐ El desenlace se dice con el CÓDIGO que vino y sin inventarle una
         * causa. `modal-carga.tsx` ya dejó el argumento: «una razón inventada es
         * peor que ninguna, **porque el operador la va a creer**».
         *
         * Los códigos que este plano puede producir son los de `AP29`: `401` de
         * sesión (`ADMIN_API_STATUS.noSession`, lo pone el enrutador antes del
         * handler), `400` de `multipart` mal formado o brazo que la instancia no
         * declaró, `405`, `503` de instancia sin capacidad de ingesta.
         * ⛔ **Ninguno es `PARTICIPANTS_API_STATUS.invalidCredential`**: en este
         * plano no hay credencial de máquina, y ese cuerpo mandaría al operador a
         * conseguir el secreto de Elige.
         */
        throw errorDelBorde(
          await mensajeDeError(
            respuesta,
            `El servidor respondió ${respuesta.status} al aceptar la carga.`,
          ),
          respuesta.status,
        )
      }

      const uploadId = leerUploadId(await respuesta.json())
      if (uploadId === null) {
        /**
         * Sin `uploadId` **no hay forma de pedir el reporte nunca**: `ADR-0109`
         * ② lo dice —«sin este campo, la ruta del reporte es una ruta con una
         * clave que nadie tiene»— y no se deriva de `(arm, lote)` a propósito.
         * Así que esto es un fallo del pedido y se dice como tal, en vez de
         * dejar al operador con una pantalla que dice «cargado» y una evidencia
         * inalcanzable.
         */
        throw errorDelBorde(
          "El servidor aceptó la carga pero no devolvió el identificador con el que se pide su reporte.",
          502,
        )
      }
      return uploadId
    },

    /**
     * `GET cargas` — las cargas del brazo, para la lista (`CA-91`).
     *
     * ⛔ **El `200` de esta ruta no tiene símbolo de sesión propio, y se dice
     * acá.** La lista es otra ruta que inventa `admin-web` —igual que
     * `RUTA_CARGAS`— y `PARTICIPANTS_SESSION_STATUS` no declara un código para
     * ella: `reportFound` significa «el reporte de ESTA carga», que es otra cosa.
     * Un ancla de utilería ahí se verificaría contra sí misma y no podría ponerse
     * roja nunca; por eso se usa `respuesta.ok` y queda dicho que **no lo protege
     * nada**.
     */
    async listarCargas(): Promise<readonly Carga[]> {
      const respuesta = await pedir(rutaDeLasCargas(), {
        method: "GET",
        headers: { Accept: "application/json" },
      })

      if (!respuesta.ok) {
        throw errorDelBorde(
          await mensajeDeError(
            respuesta,
            `El servidor respondió ${respuesta.status} al listar las cargas.`,
          ),
          respuesta.status,
        )
      }

      const cargas = leerCargas(await respuesta.json())
      if (cargas === null) {
        throw errorDelBorde(
          "El servidor contestó la lista de cargas en una forma que no se entiende.",
          502,
        )
      }
      return cargas
    },

    /**
     * `GET cargas/{uploadId}/reporte` — el reporte de UNA carga.
     *
     * ⭐⛔ **`null` es «TODAVÍA NO».** Ver `REPORTE_TODAVIA_NO`: el `202` promete
     * RECIBIR y ENCOLAR, el worker corre después (`ADR-0044`), y el contrato
     * declara que este `404` **no distingue «todavía no» de «nunca va a
     * existir»**. Traducirlo acá a un error, o a un «esta carga no produjo
     * reporte», sería que la pantalla afirme —con la autoridad de un mensaje del
     * sistema— algo que el sistema explícitamente se negó a afirmar.
     */
    async leerReporte(uploadId: IngestUploadId): Promise<ReporteDeCarga | null> {
      const ruta = rutaDeLasCargas(`/${encodeURIComponent(uploadId)}/${SEGMENTO_REPORTE}`)
      const respuesta = await pedir(ruta, {
        method: "GET",
        headers: { Accept: "application/json" },
      })

      if (respuesta.status === REPORTE_TODAVIA_NO) return null

      if (!respuesta.ok) {
        throw errorDelBorde(
          await mensajeDeError(
            respuesta,
            `El servidor respondió ${respuesta.status} al pedir el reporte.`,
          ),
          respuesta.status,
        )
      }

      const carga = leerReporteDeCarga(await respuesta.json())
      if (carga === null) {
        throw errorDelBorde(
          "El servidor contestó el reporte en una forma que no se entiende.",
          502,
        )
      }
      return carga
    },
  }
}
